import { FIELD_KINDS, PILE_KINDS, cardAt } from './_shared';

/**
 * **カードが動いた**（索引 #557 の分類。Issue #589 で共通側へ）。
 *
 * | variant | 意味 |
 * |---|---|
 * | `destroy` | 破壊（場から） |
 * | `destroy_elsewhere` | 破壊（手札・デッキなど場以外のカード） |
 * | `leave` | 場から墓地・手札・除外・デッキへ（破壊・素材・発動後以外） |
 * | `elsewhere` | 場を経由せず墓地・手札・除外・デッキへ（サーチ・捨てる・落とす・戻す） |
 * | `material` | 素材系（シンクロ・融合・儀式・エクシーズ・リンク・**アドバンス召喚のリリース**。ユーザー 2026-09-28） |
 * | `enter` | 場に出る（召喚・セット・発動のために置く） |
 * | `after_activation` | 発動し終えた魔法・罠が墓地へ |
 *
 * 事実: `prev`（移動前の姿。割れる前の表裏・向き）/ `next`（置かれたあとの姿。`enter` だけ）/
 * `countBefore`（出どころ・行き先の動く前の枚数。`elsewhere` だけ）
 */
export const VARIANTS = Object.freeze([
  { name: 'destroy', label: '破壊（場から）' },
  { name: 'destroy_elsewhere', label: '破壊（手札・デッキなど場以外のカード）' },
  { name: 'leave', label: '場から墓地・手札・除外・デッキへ' },
  { name: 'elsewhere', label: '場を経由せず墓地・手札・除外・デッキへ' },
  { name: 'material', label: '素材・リリース' },
  { name: 'enter', label: '場に出る' },
  { name: 'after_activation', label: '発動後（魔法・罠が墓地へ）' },
]);

/** 移動の分類（payload だけで決まる。分からなければ null） */
export function classify(p) {
  if (!p?.fromZone) return null;
  const fromField = FIELD_KINDS.includes(p.fromKind);
  const toField = FIELD_KINDS.includes(p.toKind);
  if (p.isMaterial || p.isRelease) return 'material';
  if (p.isDestroy) return fromField ? 'destroy' : 'destroy_elsewhere';
  if (fromField) {
    if (toField) return 'enter';
    if (p.fromKind === 'magic_trap' && p.toKind === 'grave' && p.isRule) return 'after_activation';
    if (PILE_KINDS.includes(p.toKind)) return 'leave';
    return null;
  }
  if (toField) return 'enter';
  return PILE_KINDS.includes(p.toKind) ? 'elsewhere' : null;
}

export default Object.freeze({
  event: 'move',
  variants: VARIANTS,
  classify({ payload, board, boardBefore }) {
    const variant = classify(payload);
    if (!variant) return null;
    // 割れる前の絵（表裏・向き）は移動前の盤面から
    const c = cardAt(boardBefore, payload.uid, payload.fromZone);
    const countBefore = variant === 'elsewhere' ? {
      from: boardBefore?.count?.(payload.fromZone) ?? null, to: boardBefore?.count?.(payload.zone) ?? null,
    } : null;
    // 置かれたあとの姿（表裏・守備・ATK / DEF）は移動後の盤面から
    const n = variant === 'enter' ? cardAt(board, payload.uid, payload.zone) : null;
    return {
      ...payload,
      variant,
      prev: { code: c?.code ?? payload.code ?? null, faceUp: c ? Boolean(c.faceUp) : true, defense: Boolean(c?.defense) },
      ...(countBefore ? { countBefore } : {}),
      ...(n ? {
        next: { code: n.code ?? payload.code ?? null, faceUp: Boolean(n.faceUp), defense: Boolean(n.defense), stats: n.stats ?? null },
      } : {}),
    };
  },
});
